import { useState, useEffect, useCallback } from "react";

const citySlides = [
  {
    id: 1,
    city: "New York City",
    country: "USA",
    image: "https://public.readdy.ai/ai/img_res/706b09f7388a0466b4c92aa6130f4110.jpg",
  },
  {
    id: 2,
    city: "Miami, Florida",
    country: "USA",
    image: "https://readdy.ai/api/search-image?query=Miami%20Florida%20South%20Beach%20bright%20sunny%20daytime%20blue%20ocean%20palm%20trees%20colorful%20art%20deco%20buildings%20aerial%20view%20vibrant%20wide%20panoramic%20photography&width=1600&height=900&seq=city-miami&orientation=landscape",
  },
  {
    id: 3,
    city: "Central Park",
    country: "New York, USA",
    image: "https://readdy.ai/api/search-image?query=Central%20Park%20New%20York%20bright%20sunny%20daytime%20green%20trees%20blue%20sky%20aerial%20view%20Manhattan%20skyline%20surrounding%20lush%20vibrant%20wide%20panoramic%20photography&width=1600&height=900&seq=city-centralpark&orientation=landscape",
  },
  {
    id: 4,
    city: "California",
    country: "USA",
    image: "https://readdy.ai/api/search-image?query=California%20Golden%20Gate%20Bridge%20San%20Francisco%20bright%20sunny%20daytime%20blue%20sky%20bay%20water%20golden%20hills%20vibrant%20wide%20panoramic%20photography&width=1600&height=900&seq=city-california&orientation=landscape",
  },
  {
    id: 5,
    city: "London",
    country: "United Kingdom",
    image: "https://readdy.ai/api/search-image?query=London%20Bridge%20Thames%20River%20bright%20sunny%20daytime%20blue%20sky%20Tower%20Bridge%20iconic%20landmark%20vibrant%20colorful%20wide%20panoramic%20photography&width=1600&height=900&seq=city-london&orientation=landscape",
  },
  {
    id: 6,
    city: "Paris",
    country: "France",
    image: "https://readdy.ai/api/search-image?query=Paris%20Eiffel%20Tower%20bright%20sunny%20daytime%20blue%20sky%20Seine%20River%20city%20view%20vibrant%20colorful%20wide%20panoramic%20photography%20golden%20light&width=1600&height=900&seq=city-paris&orientation=landscape",
  },
  {
    id: 7,
    city: "Sydney",
    country: "Australia",
    image: "https://readdy.ai/api/search-image?query=Sydney%20Opera%20House%20Harbour%20Bridge%20bright%20sunny%20daytime%20blue%20sky%20blue%20water%20vibrant%20colorful%20wide%20panoramic%20photography%20Australia&width=1600&height=900&seq=city-sydney&orientation=landscape",
  },
  {
    id: 8,
    city: "Shanghai",
    country: "China",
    image: "https://readdy.ai/api/search-image?query=Shanghai%20Pudong%20skyline%20bright%20sunny%20daytime%20blue%20sky%20Oriental%20Pearl%20Tower%20modern%20skyscrapers%20Huangpu%20River%20vibrant%20wide%20panoramic%20photography&width=1600&height=900&seq=city-shanghai&orientation=landscape",
  },
  {
    id: 9,
    city: "Tokyo",
    country: "Japan",
    image: "https://readdy.ai/api/search-image?query=Tokyo%20Japan%20bright%20sunny%20daytime%20blue%20sky%20Mount%20Fuji%20skyline%20cherry%20blossom%20modern%20city%20vibrant%20colorful%20wide%20panoramic%20photography&width=1600&height=900&seq=city-tokyo&orientation=landscape",
  },
  {
    id: 10,
    city: "Dubai",
    country: "UAE",
    image: "https://readdy.ai/api/search-image?query=Dubai%20city%20bright%20sunny%20daytime%20blue%20sky%20Burj%20Khalifa%20skyscrapers%20desert%20modern%20architecture%20vibrant%20colorful%20wide%20panoramic%20photography%20aerial%20view&width=1600&height=900&seq=city-dubai&orientation=landscape",
  },
];

export default function HeroSection() {
  const [jobQuery, setJobQuery] = useState("");
  const [location, setLocation] = useState("");
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((idx: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 400);
  }, [animating]);

  const next = useCallback(() => {
    goTo((current + 1) % citySlides.length);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + citySlides.length) % citySlides.length);
  }, [current, goTo]);

  // Auto-play like a short video — 4 seconds per slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % citySlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const slide = citySlides[current];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Slides */}
      {citySlides.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0, zIndex: i === current ? 1 : 0 }}
        >
          <img
            src={s.image}
            alt={s.city}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/25 z-10"></div>

      {/* City Label */}
      <div className="absolute top-24 right-6 md:right-10 z-20 flex items-center gap-2 bg-black/30 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
        <i className="ri-map-pin-fill text-amber-400 text-sm"></i>
        <span className="text-white text-sm font-semibold">{slide.city}</span>
        <span className="text-white/60 text-xs">· {slide.country}</span>
      </div>

      {/* Content */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-vip-crown-fill text-amber-400 text-sm"></i>
            <span className="text-amber-300 text-sm font-medium">Crown Ad &amp; ZhaoPin — Official Global Portal</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-4">
            The Best <span className="text-amber-400">Crown</span><br />Platform in the World
          </h1>
          <p className="text-white/80 text-lg md:text-xl mb-4 leading-relaxed">
            Serving New York · Florida · Vancouver · Toronto · London · Paris · Dubai · Sydney · Tokyo · and more — the #1 platform across USA, Canada, Europe, Asia &amp; Australia.
          </p>
          {/* City pills */}
          <div className="flex flex-wrap gap-2 mb-8">
            {["🇺🇸 New York", "🇺🇸 Florida", "🇨🇦 Vancouver", "🇨🇦 Toronto", "🇬🇧 London", "🇫🇷 Paris", "🇦🇪 Dubai", "🇦🇺 Sydney"].map((city) => (
              <span key={city} className="text-xs bg-white/15 border border-white/25 text-white px-3 py-1 rounded-full backdrop-blur-sm font-medium whitespace-nowrap">
                {city}
              </span>
            ))}
          </div>

          {/* Search Box */}
          <div className="bg-white rounded-xl p-3 md:p-4 shadow-2xl mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex items-center gap-3 bg-stone-50 rounded-lg px-4 py-3">
                <i className="ri-search-line text-stone-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="Job title, keyword, or company"
                  value={jobQuery}
                  onChange={(e) => setJobQuery(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-gray-800 placeholder-stone-400 outline-none"
                />
              </div>
              <div className="flex-1 flex items-center gap-3 bg-stone-50 rounded-lg px-4 py-3">
                <i className="ri-map-pin-line text-stone-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="City, state, or remote"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-gray-800 placeholder-stone-400 outline-none"
                />
              </div>
              <button className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm">
                Search Jobs
              </button>
            </div>
            {/* Quick Filters */}
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-stone-100">
              <span className="text-xs text-stone-500 mr-1 self-center">Popular:</span>
              {["Remote", "Full-time", "Technology", "Finance", "Marketing", "Healthcare"].map((tag) => (
                <button
                  key={tag}
                  className="text-xs px-3 py-1 rounded-full bg-stone-100 hover:bg-amber-100 hover:text-amber-700 text-stone-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-6">
            {[
              { value: "48,200+", label: "Active Jobs" },
              { value: "3,500+", label: "Companies" },
              { value: "2.1M+", label: "Job Seekers" },
              { value: "30+", label: "Cities Worldwide" },
            ].map((stat) => (
              <div key={stat.label} className="text-white">
                <div className="text-2xl font-bold text-amber-400">{stat.value}</div>
                <div className="text-sm text-white/70">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Prev / Next Arrows */}
      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center bg-black/30 hover:bg-black/50 border border-white/20 rounded-full text-white transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center bg-black/30 hover:bg-black/50 border border-white/20 rounded-full text-white transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* Dot Indicators */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {citySlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current
                ? "w-6 h-2 bg-amber-400"
                : "w-2 h-2 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 h-0.5 bg-white/10">
        <div
          key={current}
          className="h-full bg-amber-400"
          style={{
            animation: "progressBar 4s linear forwards",
          }}
        />
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 right-6 z-20 flex flex-col items-center gap-1 animate-bounce">
        <span className="text-white/50 text-xs">Scroll</span>
        <i className="ri-arrow-down-line text-white/50 text-lg"></i>
      </div>

      <style>{`
        @keyframes progressBar {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
}
